import React, { lazy } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { useTheme } from '../providers/ThemeProvider';
import { blogPosts, BlogPostBody } from '../../data/blogPosts';
export function BlogArticlePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const post = blogPosts.find(p => p.slug === slug);
  if (!post) {
    return <div className={`min-h-screen ${isDark ? 'bg-navy-950' : 'bg-white'}`} data-id="element-3236">
        <Navbar data-id="element-3237" />
        <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-id="element-3238">
          <div className="max-w-3xl mx-auto text-center py-20" data-id="element-3239">
            <h1 className={`text-3xl font-serif font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3240">
              Article Not Found
            </h1>
            <p className={`mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3241">
              The article you're looking for doesn't exist or may have been
              moved.
            </p>
            <Link to="/blog" className={`inline-flex items-center gap-2 font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`} data-id="element-3242">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-3243" />
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer data-id="element-3244" />
      </div>;
  }
  const renderBodyBlock = (block: BlogPostBody, index: number) => {
    switch (block.type) {
      case 'heading':
        if (block.level === 2) {
          return <h2 key={index} className={`text-2xl sm:text-3xl font-serif font-bold mt-10 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3245">
              {block.text}
            </h2>;
        }
        return <h3 key={index} className={`text-xl sm:text-2xl font-serif font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3246">
            {block.text}
          </h3>;
      case 'paragraph':
        return <p key={index} className={`text-base sm:text-lg leading-relaxed mb-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3247">
            {block.text}
          </p>;
      case 'image':
        return <figure key={index} className="my-8" data-id="element-3248">
            <img src={block.src} alt={block.alt || ''} className="w-full rounded-lg object-cover max-h-[480px]" loading="lazy" data-id="element-3249" />
            {block.caption && <figcaption className={`text-sm mt-3 text-center ${isDark ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-3250">
                {block.caption}
              </figcaption>}
          </figure>;
      default:
        return null;
    }
  };
  return <div className={`min-h-screen ${isDark ? 'bg-navy-950' : 'bg-white'}`} data-id="element-3251">
      <Navbar data-id="element-3252" />

      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-id="element-3253">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mx-auto" data-id="element-3254">
          {/* Back Link */}
          <Link to="/blog" className={`inline-flex items-center gap-2 text-sm font-medium mb-8 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-3255">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3256" />
            Back to Blog
          </Link>

          {/* Article Header */}
          <header className="mb-10" data-id="element-3257">
            <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-serif font-bold leading-tight mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3258">
              {post.title}
            </h1>
            <div className={`flex items-center gap-3 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-3259">
              <span data-id="element-3260">{post.author}</span>
              <span data-id="element-3261">·</span>
              <time data-id="element-3262">{post.publishedDate}</time>
            </div>
          </header>

          {/* Hero Image */}
          <div className="mb-10 rounded-xl overflow-hidden" data-id="element-3263">
            <img src={post.thumbnailUrl} alt={post.title} className="w-full object-cover max-h-[400px]" loading="eager" data-id="element-3264" />
          </div>

          {/* Article Body */}
          <article data-id="element-3265">{post.body.map(renderBodyBlock)}</article>

          {/* Bottom Navigation */}
          <div className={`mt-16 pt-8 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-3266">
            <Link to="/blog" className={`inline-flex items-center gap-2 font-medium transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`} data-id="element-3267">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-3268" />
              Back to all articles
            </Link>
          </div>
        </motion.div>
      </main>

      <Footer data-id="element-3269" />
    </div>;
}